import { globalToastActions, globalToastsDispatch, type PricingRule } from '@talos/kyoko';
import { v4 as uuid } from 'uuid';
import { baseRestApi, createTag } from '../../../utils/rtkQuery';

export interface EffectivePricingRuleRequestParams {
  counterparty: string;
  symbol: string;
}

const salesRFQApiInternal = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    getEffectivePricingRule: builder.query<PricingRule[], EffectivePricingRuleRequestParams>({
      async queryFn(params, queryApi, extraOptions, fetchWithBaseQuery) {
        const url = getEffectivePricingRuleURL(params);
        const result = await fetchWithBaseQuery({ url, method: 'GET' });

        if (result.error) {
          globalToastsDispatch(
            globalToastActions.addToastWithTimeout({
              type: 'ui',
              value: {
                text: `Something went wrong when requesting effective pricing rule`,
                variant: 'NEGATIVE',
                id: uuid(),
                timeout: 5000,
                dismissable: true,
              },
            })
          );
          return { error: result.error };
        }

        return { data: result.data as PricingRule[] };
      },
      // This associates these tags with each request. This lets us invalidate specific tags. The id below
      // is the obvious unique tag which we use to invalidate cache entries for specific requests.
      providesTags: (_result, _error, params) => [
        createTag('EffectivePricingRule', getEffectivePricingRuleURL(params)),
      ],
    }),
  }),
});

export const salesRFQApi = {
  endpoints: salesRFQApiInternal.endpoints,
  util: salesRFQApiInternal.util,
};

/** Goes from an object of parameters into a url query string to use to make a request */
export function getEffectivePricingRuleURL(params: EffectivePricingRuleRequestParams): string {
  const { counterparty, symbol } = params;
  return `/pricing-rules/counterparty/${encodeURIComponent(counterparty)}/security/${symbol}/effective`;
}
